import * as translator from '../../../../utils/translator';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useState } from 'react';
import {
    CustomerAddressCreation,
    createCustomerAddressCreationSchema,
} from './schemas';
import Select from '../../../../components/Select/Select';
import { validatePhoneDE } from '../../../../utils/fieldsValidator';

type DeliveryAddressPackstationFormProps = {
    onSubmit: (formData: CustomerAddressCreation) => void;
    formID: string;
    language: string;
    defaultFormValues: any;
    countryOptions: any[];
};

const DeliveryAddressPackstationForm: React.FC<DeliveryAddressPackstationFormProps> = ({
    onSubmit,
    formID,
    language,
    defaultFormValues,
    countryOptions
}) => {
    // Generate the Zod schema with the current language
    const customerAddressSchema = createCustomerAddressCreationSchema(language, true);
    const [selectedCountry, setSelectedCountry] = useState("");
    const { register, handleSubmit, formState: { errors }, reset, setValue, getValues } = useForm<CustomerAddressCreation>({
        resolver: zodResolver(customerAddressSchema),
        mode: "onSubmit",
        defaultValues: defaultFormValues
    });

    useEffect(() => {
        // Reset form when address attributes change
        if (defaultFormValues) {
            //This form doesn't utilize the address field, so we need to manually set it
            reset({
                ...defaultFormValues,
                address_type: "packstation" 
            });
            setSelectedCountry(countryOptions.find(option => option.id === defaultFormValues.country_id) || countryOptions[0]);
        }
    }, [defaultFormValues, countryOptions]);

    const handleCountryChange = ({ option }: { option: any, index: number }) => {
        setValue("country_id", option.id, { shouldValidate: true });
        setSelectedCountry(option);
    };

    const inputClasses = (fieldName: string) => `
        w-full px-4 py-3 border border-solid
        ${errors[fieldName as keyof CustomerAddressCreation]
            ? 'border-red-500'
            : 'border-[#E5E5E5]'} 
        rounded-md focus:outline-none focus:ring-2 focus:ring-[#269C6E]
        placeholder:text-gray-500 text-sm
    `;


    return (
        <div id="packstation-modal" className="[&_input]:border-solid [&_input[type=button]]:border-[#269C6E] max-w-md w-full rounded-md rounded-tl-3xl rounded-br-3xl">

            {/* Form */}
            <form id={formID} className='flex flex-col gap-y-4 w-full max-w-md mx-auto' onSubmit={handleSubmit(onSubmit)}>
                <div id='packstation-form-container' className='flex flex-col gap-y-4 px-0 md:px-4'>

                    <div id="packstation-name-row" className="grid grid-cols-1 md:grid-cols-2 gap-y-4 md:gap-y-0 md:gap-x-4">
                        <div id="packstation-firstname-container">
                            <input
                                id="packstation-firstname"
                                {...register("first_name")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `forms/firstName`)}
                                className={inputClasses("first_name")}
                            />
                            {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name.message} </p>}
                               
                            
                        </div>
                        <div id="packstation-lastname-container">
                            <input
                                id="packstation-lastname"
                                {...register("last_name")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `forms/lastName`)}
                                className={inputClasses("last_name")}
                            />
                            {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name.message}</p>}
                        </div>
                    </div>

                    <div id="packstation-info-row" className="grid grid-cols-1 md:grid-cols-2 gap-y-4 md:gap-y-0 md:gap-x-4">
                        <div id="packstation-number-container">
                            <input
                                id="packstation-number"
                                {...register("packstation_number")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `forms/packstationNumber`)}
                                className={inputClasses("packstation_number")}
                            />
                            {errors.packstation_number && <p className="text-red-500 text-xs mt-1">{errors.packstation_number.message}</p>}
                        </div>
                        <div id="packstation-address-container">
                            <input
                                id="packstation-address"
                                {...register("packstation_address")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `forms/postNumber`)}
                                className={inputClasses("packstation_address")}
                            />
                            {errors.packstation_address && <p className="text-red-500 text-xs mt-1">{errors.packstation_address.message}</p>}
                        </div>
                    </div>

                    <div id="packstation-location-row" className="grid grid-cols-1 md:grid-cols-2 gap-y-4 md:gap-y-0 md:gap-x-4">
                        <div id="packstation-zip-container">
                            <input
                                id="packstation-zip"
                                {...register("zip")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `forms/zip`)}
                                className={inputClasses("zip")}
                            />
                            {errors.zip && <p className="text-red-500 text-xs mt-1">{errors.zip.message}</p>}

                        </div>
                        <div id="packstation-city-container">
                            <input
                                id="packstation-city"
                                {...register("city")}
                                type="text"
                                placeholder={translator.translate(language, "my_account", `forms/city`)}
                                className={inputClasses("city")}
                            />
                            {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city.message} </p>}

                        </div>
                    </div>

                    <div id="packstation-country-container">
                        <Select
                            id="packstation-country"
                            options={countryOptions as never[]}
                            initialValue={selectedCountry}
                            onChangeCallback={handleCountryChange}
                            disabled={countryOptions.length === 1}
                            placeholder={translator.translate(language, "shipping", "ShippingForm/country")}
                            errorMode={false}
                            style={{
                                border: "1px solid #E5E5E5",
                                height: "44px",
                                width: "100%",
                                borderRadius: "7px",
                                backgroundColor: "white",
                                margin: "0px"
                            }}
                        />
                    </div>

                    <div id="packstation-phone-container">
                        <input
                            id="packstation-phone"
                            {...register("phone", {
                                onBlur: (e) => {
                                    const val = e.target.value;
                                    if (val && getValues("country_id")?.toLowerCase() === 'de') {
                                        const result = validatePhoneDE(val, language);
                                        if (result.isValid && result.e164 && result.e164 !== val) {
                                            setValue("phone", result.e164);
                                        }
                                    }
                                }
                            })}
                            type="tel"
                            placeholder={translator.translate(language, "my_account", `forms/phone`)}
                            className={inputClasses("phone")}
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                        <p id="packstation-phone-hint" className="mt-1 text-xs text-[#9D9D9D]">{translator.translate(language, "my_account", `DeliveryAddress/note`)}</p>
                    </div>

                    <div id="packstation-default-container" className="mt-2">
                        <label id="packstation-default-label" className="inline-flex items-center">
                            <input
                                id="packstation-default"
                                {...register("is_default")}
                                type="checkbox"
                                className="!block rounded text-[#269C6E] focus:ring-[#269C6E] checked:bg-[#269C6E] checked:text-white border-solid border border-gray-300"
                            />
                            <span id="packstation-default-text" className="ml-2 text-[#9D9D9D] text-xs font-medium tracking-wider">
                                {translator.translate(language, "my_account", `DeliveryAddressV2/saveAsDefault`)}
                            </span>
                        </label>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default DeliveryAddressPackstationForm;